import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AiOutlineCheck,
  AiOutlineClose,
  AiOutlineSearch,
} from 'react-icons/ai';
import { BiEdit, BiTrash } from 'react-icons/bi';
import { Link, useHistory } from 'react-router-dom';
import Swal from 'sweetalert2';
import BreadCrumb from '../../components/BreadCrumb';
import { CreateButtonDefaultContainer } from '../../components/CreateButtonDefaultContainer';
import DefaultButton from '../../components/DefaultButton';
import { DefaultCreateButtonAndSearchFormContainer } from '../../components/DefaultCreateButtonAndSearchFormContainer';
import DefaultFilterForm from '../../components/DefaultFilterForm';
import DefaultInput from '../../components/DefaultInput';
import { DefaultPageTitle } from '../../components/DefaultPageTitle';
import DefaultTable from '../../components/DefaultTable';
import Trail from '../../models/trail';
import {
  deleteCourse,
  getAllCourses,
  activateOrInactivateCourse as activateOrInactivateCourseService,
} from '../../services/trails';
import { CoursesContainer } from './style';

const Courses: React.FC = () => {
  const history = useHistory();

  const [courses, setCourses] = useState([] as Trail[]);
  const [courseName, setCourseName] = useState('');

  const getCourses = async (event?: React.FormEvent) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }

    const localCourses = await getAllCourses({ name: courseName });
    setCourses(localCourses);
  };

  const editCourse = useCallback(
    (courseId: any) => {
      history.push(`/course/edit-serie/${courseId}`);
    },
    [history],
  );

  const removeCourse = useCallback(async (courseId: any) => {
    Swal.fire({
      title: '<strong>Confirmação</strong>',
      html: 'Tem certeza que deseja remover esta curso?',
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      focusConfirm: false,
    }).then(async result => {
      if (result.isConfirmed) {
        try {
          await deleteCourse(courseId);

          Swal.fire({
            icon: 'success',
            title: 'Sucesso!',
            text: 'Curso excluída com sucesso!',
          });

          await getCourses();
        } catch (e) {
          Swal.fire({
            icon: 'error',
            title: 'Erro ao excluir curso',
            text: e.message,
          });
        }
      }
    });
  }, []);

  const createCourse = async () => {
    history.push('/course/create-serie');
  };

  const activateOrInactivateCourse = useCallback(
    async (courseId: any, activate: boolean) => {
      Swal.fire({
        title: 'Confirmação',
        text: `Tem certeza que deseja ${
          activate ? 'ativar' : 'inativar'
        } este curso?`,
        showCancelButton: true,
        cancelButtonText: 'Cancelar',
        focusConfirm: false,
      }).then(async result => {
        if (result.isConfirmed) {
          try {
            await activateOrInactivateCourseService(courseId, activate);

            getCourses();
          } catch (error) {
            Swal.fire({
              title: 'Erro',
              text: `Erro ao ${activate ? 'ativar' : 'inativar'} curso!`,
            });
          }
        }
      });
    },
    [],
  );

  const coursesToBeShown = useMemo(() => {
    return courses && courses.length
      ? courses.map(course => ({
          id: course.id,
          course_id: course.course_id,
          name: course.name,
          description: course.description,
          active: course.is_active ? <AiOutlineCheck /> : <AiOutlineClose />,
          actions: (
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                gap: '5px',
              }}
            >
              <DefaultButton
                className="small info"
                onClick={() => editCourse(course.trail_id)}
                title="Editar curso"
              >
                <BiEdit />
              </DefaultButton>{' '}
              <DefaultButton
                onClick={() =>
                  activateOrInactivateCourse(course.trail_id, !course.is_active)
                }
                className="small warning"
                title={(course.is_active ? 'Inativar' : 'Ativar') + ' curso'}
              >
                {course.is_active ? <AiOutlineClose /> : <AiOutlineCheck />}
              </DefaultButton>{' '}
              <DefaultButton
                onClick={() => removeCourse(course.trail_id)}
                className="small danger"
                title="Excluir Curso"
              >
                <BiTrash />
              </DefaultButton>
            </div>
          ),
        }))
      : [];
  }, [courses, editCourse, removeCourse, activateOrInactivateCourse]);

  useEffect(() => {
    getCourses();
  }, []);

  useEffect(() => {
    if (!courseName) {
      getCourses();
    }
  }, [courseName]);

  return (
    <CoursesContainer>
      <BreadCrumb
        crumbs={[
          <Link to="/profile">Dashboard</Link>,
          <span>Cursos</span>,
          <span>Curso</span>,
        ]}
      />

      <DefaultPageTitle>Cursos</DefaultPageTitle>

      <DefaultFilterForm
        style={{
          display: 'grid',
          gridTemplateColumns: '95% 5%',
          gap: '10px',
          margin: '10px 0',
        }}
      ></DefaultFilterForm>

      <DefaultCreateButtonAndSearchFormContainer>
        <CreateButtonDefaultContainer>
          <DefaultButton onClick={createCourse}>Criar Curso</DefaultButton>
        </CreateButtonDefaultContainer>

        <form
          onSubmit={getCourses}
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'stretch',
            gap: '10px',
          }}
        >
          <DefaultInput
            type="search"
            placeholder="Digite sua pesquisa aqui..."
            value={courseName}
            onChange={e => setCourseName(e.target.value)}
            style={{ margin: 0 }}
          />
          <DefaultButton
            className="small"
            style={{ margin: 0, height: '43px' }}
            type="submit"
          >
            <AiOutlineSearch size={24} />
          </DefaultButton>
        </form>
      </DefaultCreateButtonAndSearchFormContainer>

      <DefaultTable
        headersConfig={[
          {
            headerLabel: <span>Nome</span>,
            propName: 'name',
          },
          {
            headerLabel: <span>Descrição</span>,
            propName: 'description',
            attributes: { style: { width: '35%' } },
          },
          {
            headerLabel: <span>Ativo</span>,
            propName: 'active',
            order: (_: any[], currentOrder: string) => {
              setCourses([
                ...courses.sort((a, b) => {
                  const isDescendant =
                    currentOrder.includes('active') &&
                    currentOrder.includes('asc');
                  if (isDescendant) {
                    return a.is_active ? 1 : -1;
                  } else {
                    return a.is_active ? -1 : 1;
                  }
                }),
              ]);
            },
          },
          {
            headerLabel: <span>Ações</span>,
            propName: 'actions',
          },
        ]}
        items={coursesToBeShown}
        emptyListMessage={'Não foram encontradas cursos cadastradas!'}
      />
    </CoursesContainer>
  );
};

export default Courses;
